import {
  SearchParams,
  useSearchVariantPacks,
} from './use-search-variant-packs';
import {
  SearchSortOrder,
  useUserSettingsContext,
} from '@/contexts/user-context';
import { useCallback } from 'react';
import { useToasts } from '@/contexts/toast-context';

export const useSearchCatalog = ({
  availabilityFulfillmentDate,
  category,
  includeGTINFilters,
  includeOrderCounts,
  initialSearchText,
  searchSource,
}: SearchParams) => {
  const {
    badgesFilter,
    availabilityMap,
    categories,
    categoryFilter,
    categoryItemGroups,
    categoryItemGroupFilter,
    clearSearch,
    gtinFilters,
    items,
    matchingVariantPackUUIDs,
    queryLoading,
    queryRef,
    searchItemsCallback,
    setCategoryFilter,
    setCategoryItemGroupFilter,
    setBadgesFilter,
  } = useSearchVariantPacks({
    availabilityFulfillmentDate,
    category,
    includeGTINFilters,
    includeOrderCounts,
    initialSearchText,
    searchSource,
  });
  const { searchSortOrder, setSearchSortOrder } = useUserSettingsContext();
  const { showErrorToast } = useToasts();

  const searchItems = useCallback(
    async (query: string, sortOrder?: SearchSortOrder) => {
      try {
        await searchItemsCallback(query, sortOrder || searchSortOrder);
      } catch (e) {
        showErrorToast();
      }
    },
    [searchItemsCallback, searchSortOrder, showErrorToast],
  );

  const changeSortOrder = useCallback(
    async (sortOrder: SearchSortOrder) => {
      setSearchSortOrder(sortOrder);

      if (queryRef.current) {
        // Refetch search result to reflect the sort change
        await searchItems(queryRef.current, sortOrder);
      }
    },
    [setSearchSortOrder, searchItems, queryRef],
  );

  return {
    availabilityMap,
    badgesFilter,
    categories,
    categoryFilter,
    categoryItemGroupFilter,
    categoryItemGroups,
    changeSortOrder,
    clearSearch,
    gtinFilters,
    items,
    matchingVariantPackUUIDs,
    queryLoading,
    queryRef,
    searchItems,
    searchItemsCallback,
    searchSortOrder,
    setBadgesFilter,
    setCategoryFilter,
    setCategoryItemGroupFilter,
  };
};
